import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, X } from 'lucide-react';
import CreatorView from './CreatorView';
import TestCreatorView from './TestCreatorView';
import PerformanceSummaryView from './PerformanceSummaryView';
import { leitnerService } from '../../services/leitnerService';
import { db } from '../../config/firebase';
import { collection, getDocs, addDoc, doc, deleteDoc } from 'firebase/firestore';

export const LeitnerManager = ({ bookId, user, bookData, isOpen, onClose }) => {
  const [questions, setQuestions] = useState([]);
  const [tests, setTests] = useState([]);
  const [testResults, setTestResults] = useState([]);
  const [activeTab, setActiveTab] = useState('questions');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && bookId) {
      loadData();
    }
  }, [isOpen, bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const questionsData = await leitnerService.getBookQuestions(bookId);
      setQuestions(questionsData);

      const testsData = await leitnerService.getTests(bookId);
      setTests(testsData);

      const resultsData = await leitnerService.getUserTestResults(bookId);
      setTestResults(resultsData);
    } catch (error) {
      console.error('Failed to load Leitner data:', error);
    } finally {
      setLoading(false);
    }
  };

  const addQuestion = async (newQuestionData) => {
    try {
      const { stemImage, ...restOfData } = newQuestionData;
      const imageFile = stemImage instanceof File ? stemImage : null;
      
      const newQuestion = await leitnerService.addQuestion(bookId, restOfData, imageFile);
      setQuestions(prev => [newQuestion, ...prev]);
    } catch (error) {
      console.error('Failed to add question:', error);
      throw error;
    }
  };

  const updateQuestion = async (questionId, updatedQuestionData) => {
    try {
      const originalQuestion = questions.find(q => q.id === questionId);
      const { stemImage, ...restOfData } = updatedQuestionData;
      
      let imageFile = undefined; // Changed from null to undefined
      
      if (stemImage instanceof File) {
        imageFile = stemImage;
      } else if (stemImage === null) {
        imageFile = null; // Explicit deletion
      }
      // If stemImage is a URL string, imageFile stays undefined (no change)
      
      const updated = await leitnerService.updateQuestion(
        bookId, 
        questionId, 
        restOfData, 
        imageFile,
        originalQuestion?.stemImage
      );
      
      setQuestions(prev => prev.map(q => q.id === questionId ? updated : q));
    } catch (error) {
      console.error('Failed to update question:', error);
      throw error;
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const questionToDelete = questions.find(q => q.id === questionId);
      await leitnerService.deleteQuestion(
        bookId, 
        questionId, 
        questionToDelete?.stemImage
      );
      setQuestions(prev => prev.filter(q => q.id !== questionId));
    } catch (error) {
      console.error('Failed to delete question:', error);
    }
  };

  const addTest = async (newTest) => {
    try {
      const testWithId = await leitnerService.addTest(bookId, newTest);
      setTests(prev => [...prev, testWithId]);
      setActiveTab('questions');
    } catch (error) {
      console.error('Failed to add test:', error);
    }
  };

  const deleteTest = async (testId) => {
    if (window.confirm('Are you sure you want to delete this test?')) {
      try {
        await leitnerService.deleteTest(bookId, testId);
        setTests(prev => prev.filter(t => t.id !== testId));
      } catch (error) {
        console.error('Failed to delete test:', error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white shadow-xl w-full max-w-7xl h-screen flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-indigo-600" />
            Leitner Question & Test Manager
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b bg-gray-50">
          <div className="flex px-6">
            <button
              onClick={() => setActiveTab('questions')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'questions'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Questions ({questions.length})
            </button>
            <button
              onClick={() => setActiveTab('tests')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'tests'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Tests ({tests.length})
            </button>
            <button
              onClick={() => setActiveTab('performance')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'performance'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Performance
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-gray-400">Loading...</p>
            </div>
          ) : (
            <>
              {activeTab === 'questions' && (
                <CreatorView
                  bookId={bookId}
                  addQuestion={addQuestion}
                  questions={questions}
                  deleteQuestion={deleteQuestion}
                  updateQuestion={updateQuestion}
                />
              )}

              {activeTab === 'tests' && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Available Tests</h3>
                    <button
                      onClick={() => setActiveTab('create-test')}
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
                    >
                      <Plus className="h-4 w-4" />
                      Create New Test
                    </button>
                  </div>

                  {tests.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 mb-4">No tests created yet.</p>
                      <button
                        onClick={() => setActiveTab('create-test')}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                      >
                        <Plus className="h-4 w-4" />
                        Create Your First Test
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {tests.map(test => (
                        <div key={test.id} className="bg-gray-50 p-4 rounded-lg flex justify-between items-center">
                          <div>
                            <h4 className="font-semibold text-gray-900">{test.name}</h4>
                            <p className="text-sm text-gray-600">{test.questionIds.length} questions</p>
                          </div>
                          <button
                            onClick={() => deleteTest(test.id)}
                            className="text-red-500 hover:text-red-700 font-semibold"
                          >
                            Delete
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'create-test' && (
                <TestCreatorView
                  questions={questions.filter(q => q.type === 'simple')}
                  addTest={addTest}
                  onCancel={() => setActiveTab('tests')}
                />
              )}

              {activeTab === 'performance' && (
                <PerformanceSummaryView tests={tests} results={testResults} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};